import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categoryService } from '../services/api';
import ExportButton from '../components/ExportButton';
import './Categories.css';
import { Typography, Button, Card, Space, Tag, Popconfirm, Switch, Tooltip, App, Input, Tree, Modal, Form, Select, Radio, Row, Col, Segmented, Table } from 'antd';
import { App as AntdApp } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, DownOutlined, UpOutlined, FolderAddOutlined, PlusCircleOutlined, ApartmentOutlined, UnorderedListOutlined } from '@ant-design/icons';
const { Title, Text } = Typography;

const _L1_ADMIN_ORDER_FR = [
  'CONTRÔLE D\'ACCÈS & POINTAGE',
  'DÉTECTION DE GAZ',
  'DÉTECTION INCENDIE',
  'DOMOTIQUE / GRMS',
  'ÉCLAIRAGE DE SÉCURITÉ',
  'EXTINCTION',
  'INTRUSION',
  'ONDULEUR',
  'SONORISATION',
  'SYSTÈME UPS',
  'TECHNOLOGIE',
  'VIDÉOSURVEILLANCE',
];
const _ADMIN_L1_IDX = (() => {
  const m = new Map();
  _L1_ADMIN_ORDER_FR.forEach((v, i) => m.set(String(v).toLowerCase().trim(), i));
  return m;
})();
const _ASSET_KNOWN_SHORT_REGEX = /^\s*(accessoire d'accès|barrière de parking|pointeuse|tourniquet|contrôleur|logiciel|terminal|type de gaz|APC|CATÉGORIE)\s*$/i;
const _FR_STRIP = (s) => String(s || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/\s+/g, ' ');
const _ADMIN_IS_PRODUCT = (name) => {
  if (!name) return false;
  const n = String(name);
  const isShort = n.split(/\s+/).filter(Boolean).length <= 3 && n.length <= 25;
  if (_ASSET_KNOWN_SHORT_REGEX.test(n)) return false;
  if (/\b(knx|usb|wifi|zigbee|bluetooth|kva|kw|sqg|cevola|vesda|sensea|selguard|atsp|monophas|triphas|ventouse|actionneur|interface\s+knx|passerelle\s+knx|superviseur|planete|ultraled|uniled|fireray|crystalway|solista|nexitech|mdva|router)\b/i.test(n)) return true;
  if (/^(\s|)*(93[PXT]|9SX|0\s*-\s*5\s*kva|socle\s+knx|détecteur\s+iry|carte\s+réseau|carte\s+reseau|type\s+2b|type\s+3)\b/i.test(n)) return true;
  if (/^(actionneur|alimentation|baal|baas|baasl|baeh|baes|bxp|beamtech|centrale|cmsi|compteur|coupleur|crystalway|déclencheur|declencheur|détecteur|dalle\s+capacitive|diffuseur|dmcf|dof|dsb|dsme|dtv|écran\s+tactile|ecs|fireray|grille|indicateur|interface|interrupteur|kit\s+|knx-|lampe\s+portable|luminaire|module\s+|nexitech|pack\s+|panneau|passerelle\s|planète|régulateur|replica|router\s+|s3000|sensea|socle\s|solista|source\s|spsm|spsmc|superviseur|système\s+audio|télécommande|tableau\s+de\s+report|ultraled|uniled|variateur|ventouse|vesda|x\d{3,}|sqg\d|cevola|zs\d)/i.test(n)) return true;
  if (/\b\d+\s*(canal(aux)?|voies?|points?|sorties?|entrées?)\b/i.test(n)) return true;
  if (/\b\d+\s*(kva|kw|ah|w|v|ma|amp)\b/i.test(n)) return true;
  if (/\b(ip\s*\d{2,3}|c13|c19|ip\d{2,3}\s+ik\d{2,3})\b/i.test(n)) return true;
  if (/\d{3,}\s*lm\b/i.test(n)) return true;
  const digitGroups = n.match(/\d+/g) || [];
  if (!isShort && digitGroups.length >= 3) return true;
  return false;
};
const _RANK_NODE = (n, isRoot) => {
  if (!n) return 999999;
  const t = n.type || null;
  const nm = String(n.name || '').trim();
  const nmFolded = _FR_STRIP(nm);
  const isLegacyProduct = !!(n.legacy === true || (!t && _ADMIN_IS_PRODUCT(nm)));
  let bucket = 5000;
  let sub = 0;
  if (isRoot) {
    const cleanTitle = nm.toLowerCase().trim().replace(/\s+/g, ' ');
    if (_ADMIN_L1_IDX.has(cleanTitle)) { bucket = 0; sub = _ADMIN_L1_IDX.get(cleanTitle) * 100; }
    else if (_ASSET_KNOWN_SHORT_REGEX.test(nm) && !isLegacyProduct) { bucket = 500; }
    else if (nmFolded === 'apc' || nmFolded === 'categorie' || nmFolded === 'catégorie') { bucket = 600; }
  }
  if (bucket > 500) {
    if (t === 'family') bucket = Math.min(bucket, 1000);
    else if (t === 'criterion') bucket = Math.min(bucket, 2000);
    else if (t === 'value') bucket = Math.min(bucket, 3000);
  }
  if (isLegacyProduct) bucket = Math.max(bucket, 9000);
  if (n.isActive === false || n.isActive === 0) bucket += 1000;
  const so = Number.isFinite(Number(n.sortOrder)) ? Math.max(0, Math.min(999, Number(n.sortOrder))) : 0;
  sub += so;
  return bucket * 100000 + sub;
};
const sortAdminTree = (nodes, isRoot = false) => {
  if (!Array.isArray(nodes)) return [];
  const sorted = [...nodes].sort((a, b) => {
    const ra = _RANK_NODE(a, isRoot);
    const rb = _RANK_NODE(b, isRoot);
    if (ra !== rb) return ra - rb;
    const sa = Number.isFinite(Number(a?.sortOrder)) ? Number(a.sortOrder) : 0;
    const sb = Number.isFinite(Number(b?.sortOrder)) ? Number(b.sortOrder) : 0;
    if (sa !== sb) return sa - sb;
    return _FR_STRIP(a?.name || '').localeCompare(_FR_STRIP(b?.name || ''), 'fr');
  });
  for (const n of sorted) {
    const kids = n.children || n.subcategories || [];
    if (Array.isArray(kids) && kids.length) {
      const s = sortAdminTree(kids, false);
      n.children = s;
      n.subcategories = s;
      n.hasChildren = true;
    }
  }
  return sorted;
};
const sortAdminFlat = (arr) => {
  if (!Array.isArray(arr)) return [];
  return [...arr].sort((a, b) => {
    const isRa = !(a.parentCategoryId || (a.parentCategory && a.parentCategory.id));
    const isRb = !(b.parentCategoryId || (b.parentCategory && b.parentCategory.id));
    const ra = _RANK_NODE(a, isRa);
    const rb = _RANK_NODE(b, isRb);
    if (ra !== rb) return ra - rb;
    const sa = Number.isFinite(Number(a?.sortOrder)) ? Number(a.sortOrder) : 0;
    const sb = Number.isFinite(Number(b?.sortOrder)) ? Number(b.sortOrder) : 0;
    if (sa !== sb) return sa - sb;
    return _FR_STRIP(a?.name || '').localeCompare(_FR_STRIP(b?.name || ''), 'fr');
  });
};
const _FR_KNOWN_GOOD = /^\s*(accessoire d'accès|barrière de parking|pointeuse|tourniquet|contrôleur|logiciel|terminal|type de gaz|apc|catégorie|contrôle d'accès\s*&\s*pointage|détection de gaz|détection incendie|domotique\s*\/\s*grms|éclairage de sécurité|extinction|intrusion|onduleur|sonorisation|système ups|technologie|vidéosurveillance)\s*$/i;
const _ADMIN_KEEP = (n) => {
  if (!n || !n.name) return false;
  const t = n.type || null;
  const nm = String(n.name || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').trim();
  if (_FR_KNOWN_GOOD.test(String(n.name || '').trim())) return true;
  if (t === 'family' || t === 'criterion' || t === 'value') return true;
  if (_ADMIN_IS_PRODUCT(n.name)) return false;
  return true;
};
const adminFilterOnlyCategoriesTree = (roots) => {
  if (!Array.isArray(roots)) return [];
  const walk = (nodes) => {
    const out = [];
    for (const n of nodes) {
      if (!_ADMIN_KEEP(n)) continue;
      const node = { ...n };
      const kids = node.children || node.subcategories || [];
      if (Array.isArray(kids) && kids.length) {
        const filtered = walk(kids);
        node.children = filtered;
        node.subcategories = filtered;
        node.hasChildren = filtered.length > 0;
      }
      out.push(node);
    }
    return out;
  };
  return walk(roots);
};
const adminFilterOnlyCategoriesFlat = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.filter(_ADMIN_KEEP);
};
const _FR_FFFD_WORD_MAP = [
  [/\bSyst(?:\uFFFD|'?)me\b/gi, 'Système'],
  [/\bSyst(?:\uFFFD|'?)mes\b/gi, 'Systèmes'],
  [/D(?:\uFFFD|'?)tection\b/gi, 'Détection'],
  [/D(?:\uFFFD|'?)clencheur\b/gi, 'Déclencheur'],
  [/\uFFFDclairage\b/gi, 'Éclairage'],
  [/\uFFFDcran\b/gi, 'Écran'],
  [/Acc(?:\uFFFD|'?)s\b/gi, 'Accès'],
  [/vid(?:\uFFFD|'?)o\b/gi, 'vidéo'],
  [/CAT(?:\uFFFD|'?)GORIE\b/g, 'CATÉGORIE'],
  [/Cat(?:\uFFFD|'?)gorie\b/g, 'Catégorie'],
  [/cat(?:\uFFFD|'?)gorie\b/g, 'catégorie'],
  [/G(?:\uFFFD|'?)nie\b/gi, 'Génie'],
  [/S(?:\uFFFD|'?)curit(?:\uFFFD|'?)\b/gi, 'Sécurité'],
  [/\uFFFDnergie\b/gi, 'Énergie'],
  [/\uFFFDlectronique\b/gi, 'Électronique'],
  [/R(?:\uFFFD|'?)gulateur\b/gi, 'Régulateur'],
  [/T(?:\uFFFD|'?)l(?:\uFFFD|'?)commande\b/gi, 'Télécommande'],
];
const repairAccentsFFFD = (s) => {
  if (!s) return s;
  let out = String(s);
  for (const [re, rep] of _FR_FFFD_WORD_MAP) {
    out = out.replace(re, (m) => {
      const firstUpper = /^[A-Z]/.test(m) && !/^[A-Z]{2}/.test(m);
      const allUpper = /^[A-Z]{2,}/.test(m);
      if (allUpper) return rep.toUpperCase();
      if (firstUpper) return rep.charAt(0).toUpperCase() + rep.slice(1);
      return rep;
    });
  }
  return out;
};
const repairCategoryNode = (n) => {
  if (!n) return n;
  const node = { ...n };
  if (typeof node.name === 'string') node.name = repairAccentsFFFD(node.name);
  if (typeof node.description === 'string') node.description = repairAccentsFFFD(node.description);
  if (typeof node.slug === 'string') node.slug = repairAccentsFFFD(node.slug);
  if (node.parentCategory && typeof node.parentCategory.name === 'string') {
    node.parentCategory = { ...node.parentCategory, name: repairAccentsFFFD(node.parentCategory.name) };
  }
  const kids = node.children || node.subcategories;
  if (Array.isArray(kids) && kids.length) {
    const rep = kids.map(repairCategoryNode);
    node.children = rep;
    node.subcategories = rep;
  }
  return node;
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [flatAllCategories, setFlatAllCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('tree');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [openNodes, setOpenNodes] = useState(() => new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [parentId, setParentId] = useState(null);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();
  const { message: appMsg, modal: appModal } = AntdApp.useApp();

  const getAllIds = (nodes) => {
    let out = [];
    (nodes || []).forEach((n) => {
      out.push(n.id);
      if (n.children) out = out.concat(getAllIds(n.children));
      else if (n.subcategories) out = out.concat(getAllIds(n.subcategories));
    });
    return out;
  };

  const getChildren = (n) => n?.children || n?.subcategories || [];

  const findNodeById = (nodes, id) => {
    for (const n of nodes || []) {
      if (n.id === id) return n;
      const found = findNodeById(getChildren(n), id);
      if (found) return found;
    }
    return null;
  };

  const mapTreeRecursive = (nodes, mapper) => (nodes || []).map(n => {
    const mapped = mapper(n);
    const kids = getChildren(n);
    const newKids = kids.length ? mapTreeRecursive(kids, mapper) : undefined;
    return { ...mapped, children: newKids, subcategories: newKids };
  });

  const loadCategories = async () => {
    try {
      setIsLoading(true);
      const [treeRes, flatRes] = await Promise.all([
        categoryService.getCategories({ includeInactive, tree: viewMode === 'tree' }),
        categoryService.getCategories({ includeInactive: true, tree: false })
      ]);
      let treeCats = treeRes.categories || treeRes.data || [];
      let flatCats = flatRes.categories || flatRes.data || [];
      if (Array.isArray(treeCats)) treeCats = treeCats.map(repairCategoryNode);
      if (Array.isArray(flatCats)) flatCats = flatCats.map(repairCategoryNode);
      treeCats = sortAdminTree(treeCats, true);
      treeCats = adminFilterOnlyCategoriesTree(treeCats);
      flatCats = sortAdminFlat(flatCats);
      flatCats = adminFilterOnlyCategoriesFlat(flatCats);
      setCategories(treeCats);
      setFlatAllCategories(flatCats);
      if (viewMode === 'tree') {
        const allIds = getAllIds(treeCats);
        setOpenNodes(new Set(allIds));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      appMsg.error('Erreur lors du chargement des catégories');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, [viewMode, includeInactive]);

  const openCreateModal = (pid = null) => {
    setParentId(pid);
    setEditMode(null);
    form.resetFields();
    let defaultType = null;
    if (pid === null) {
      defaultType = 'family';
    } else {
      const parent = findNodeById(viewMode === 'tree' ? categories : flatAllCategories, pid);
      if (parent) {
        if (parent.type === 'family') defaultType = 'criterion';
        else if (parent.type === 'criterion') defaultType = 'value';
      }
    }
    form.setFieldsValue({
      isActive: true,
      type: defaultType,
      parentCategory: pid === null ? '' : pid
    });
    setModalOpen(true);
  };

  const openEditModal = (node) => {
    setEditMode(node);
    setParentId(node.parentCategory || null);
    form.setFieldsValue({
      name: node.name,
      slug: node.slug || '',
      description: node.description || '',
      type: node.type || null,
      color: node.color || '#1677ff',
      icon: node.icon || '',
      isActive: node.isActive !== undefined ? node.isActive : true,
      sortOrder: node.sortOrder || 0,
      parentCategory: node.parentCategory || ''
    });
    setModalOpen(true);
  };

  const handleToggleActive = async (node) => {
    const oldIsActive = !!node.isActive;
    const newIsActive = !oldIsActive;
    // ✅ Optimistic update: modifier state LOCAL 0ms APRÈS CLIC avant réponse API
    setCategories(prev => mapTreeRecursive(prev, n => n.id === node.id ? { ...n, isActive: newIsActive } : n));
    setFlatAllCategories(prev => prev.map(row => row.id === node.id ? { ...row, isActive: newIsActive } : row));
    try {
      await categoryService.updateCategory(node.id, { isActive: newIsActive });
      appMsg.success('Statut mis à jour');
    } catch (error) {
      console.error(error);
      appMsg.error('Erreur lors de la mise à jour');
      // 🔄 Rollback en cas d'erreur API
      setCategories(prev => mapTreeRecursive(prev, n => n.id === node.id ? { ...n, isActive: oldIsActive } : n));
      setFlatAllCategories(prev => prev.map(row => row.id === node.id ? { ...row, isActive: oldIsActive } : row));
    }
  };

  const handleDelete = async (node) => {
    try {
      await categoryService.deleteCategory(node.id);
      appMsg.success('Supprimé');
      loadCategories();
    } catch (error) {
      console.error(error);
      appMsg.error('Erreur lors de la suppression');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values };
      if (payload.parentCategory === '' || payload.parentCategory === null || payload.parentCategory === undefined) {
        delete payload.parentCategory;
      }
      if (payload.type === '' || payload.type === null) {
        payload.type = null;
      }
      if (editMode) {
        await categoryService.updateCategory(editMode.id, payload);
        appMsg.success('Catégorie mise à jour');
      } else {
        if (parentId && !payload.parentCategory) {
          payload.parentCategory = parentId;
        }
        await categoryService.createCategory(payload);
        appMsg.success('Catégorie créée');
      }
      setModalOpen(false);
      loadCategories();
    } catch (error) {
      if (error?.errorFields) return;
      console.error(error);
      appMsg.error(editMode ? 'Erreur de mise à jour' : 'Erreur de création');
    }
  };

  const filterNodes = (nodes, term) => {
    if (!term) return nodes;
    const lower = term.toLowerCase();
    const filterRec = (arr) => {
      const result = [];
      for (const n of arr || []) {
        const matchSelf = (n.name || '').toLowerCase().includes(lower) || (n.slug || '').toLowerCase().includes(lower);
        const children = getChildren(n);
        const filteredChildren = children.length ? filterRec(children) : [];
        if (matchSelf || filteredChildren.length) {
          result.push({ ...n, children: filteredChildren.length ? filteredChildren : undefined, subcategories: filteredChildren.length ? filteredChildren : undefined });
        }
      }
      return result;
    };
    return filterRec(nodes);
  };

  const filterFlat = (arr, term) => {
    if (!term) return arr;
    const lower = term.toLowerCase();
    return (arr || []).filter((n) =>
      (n.name || '').toLowerCase().includes(lower) || (n.slug || '').toLowerCase().includes(lower)
    );
  };

  const typeIcon = (t) => t === 'family' ? '🏠' : t === 'criterion' ? '📁' : t === 'value' ? '🍃' : '📦';
  const typeLabel = (t) => t === 'family' ? 'Famille' : t === 'criterion' ? 'Critère' : t === 'value' ? 'Valeur' : 'legacy';
  const typeColor = (t) => t === 'family' ? 'blue' : t === 'criterion' ? 'purple' : t === 'value' ? 'cyan' : 'default';

  const renderActions = (node) => (
    <Space size={4} style={{ marginLeft: 'auto' }}>
      <Tooltip title="Ajouter un enfant">
        <Button size="small" type="primary" ghost icon={<PlusCircleOutlined />} onClick={(e) => { if (e && typeof e.stopPropagation === 'function') e.stopPropagation(); openCreateModal(node.id); }} />
      </Tooltip>
      <Tooltip title="Modifier">
        <Link to={`/admin/categories/${node.id}/edit`} onClick={(e) => { if (e && typeof e.stopPropagation === 'function') e.stopPropagation(); }}>
          <Button size="small" type="text" icon={<EditOutlined />} />
        </Link>
      </Tooltip>
      <Tooltip title={node.isActive ? 'Désactiver' : 'Activer'}>
        <Switch checked={!!node.isActive} size="small" onClick={(e) => { if (e && typeof e.stopPropagation === 'function') e.stopPropagation(); }} onChange={() => handleToggleActive(node)} />
      </Tooltip>
      <Popconfirm
        title={`Supprimer "${node.name}" ?`}
        description="Cette action est irréversible."
        onConfirm={(e) => { if (e && typeof e.stopPropagation === 'function') e.stopPropagation(); handleDelete(node); }}
        onCancel={(e) => { if (e && typeof e.stopPropagation === 'function') e.stopPropagation(); }}
      >
        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={(e) => { if (e && typeof e.stopPropagation === 'function') e.stopPropagation(); }} />
      </Popconfirm>
    </Space>
  );

  const renderNodeTitle = (node) => (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '2px 0' }}>
      <Space size={8} align="center" style={{ minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 16 }}>{typeIcon(node.type)}</span>
        <Text strong style={{ fontSize: 14 }}>{node.name}</Text>
        {node.slug && <Tag color={node.color || undefined} style={{ marginLeft: 4 }}>/{node.slug}</Tag>}
        <Tag color={node.isActive ? 'green' : 'default'}>{node.isActive ? 'Actif' : 'Inactif'}</Tag>
        <Tag color={typeColor(node.type)}>{typeLabel(node.type)}</Tag>
        {typeof node.productCount === 'number' && node.productCount > 0 && (
          <Tag color="orange">{node.productCount} produit{node.productCount > 1 ? 's' : ''}</Tag>
        )}
      </Space>
      {renderActions(node)}
    </div>
  );

  const buildAntdTreeData = (nodes) => {
    const filtered = filterNodes(nodes, search);
    return (filtered || []).map((node) => ({
      key: String(node.id),
      title: renderNodeTitle(node),
      children: getChildren(node).length ? buildAntdTreeData(getChildren(node)) : undefined
    }));
  };

  const parentSelectOptions = [
    { value: '', label: '🏠 Racine (aucun parent)' },
    ...(flatAllCategories || []).map((c) => ({
      value: c.id,
      label: `${typeIcon(c.type)} ${c.name}${c.slug ? ` (/${c.slug})` : ''}`
    }))
  ];

  const listColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      width: 90,
      render: (t) => (
        <Tag color={typeColor(t)} icon={<span>{typeIcon(t)}</span>}>{typeLabel(t)}</Tag>
      )
    },
    {
      title: 'Catégorie',
      dataIndex: 'name',
      render: (_, cat) => (
        <Space align="center">
          <span style={{ fontSize: 18 }}>{typeIcon(cat.type)}</span>
          <div>
            <div style={{ fontWeight: 500 }}>{cat.name}</div>
            <Text type="secondary">/{cat.slug || '-'}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      width: 100,
      render: (_, cat) => (
        <Tag color={cat.isActive ? 'green' : 'default'}>{cat.isActive ? 'Actif' : 'Inactif'}</Tag>
      )
    },
    {
      title: 'Produits',
      dataIndex: 'productCount',
      width: 90,
      align: 'right',
      render: (v) => <span style={{ fontWeight: 600 }}>{v || 0}</span>
    },
    {
      title: 'Actions',
      width: 260,
      render: (_, cat) => renderActions(cat)
    }
  ];

  if (isLoading && categories.length === 0) {
    return (
      <div className="categories-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement des catégories...</p>
        </div>
      </div>
    );
  }

  const expandAll = () => setOpenNodes(new Set(getAllIds(categories)));
  const collapseAll = () => setOpenNodes(new Set());

  return (
    <div className="categories-page">
      <Card variant="borderless" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <Title level={3} style={{ marginBottom: 4 }}>
              <ApartmentOutlined style={{ marginRight: 8 }} />
              Gestion des Catégories & Critères
            </Title>
            <Text type="secondary">Source unique de vérité → catégories + filtres + sélection produit</Text>
          </div>
          <Space wrap>
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                { label: <span><ApartmentOutlined /> Arbre</span>, value: 'tree' },
                { label: <span><UnorderedListOutlined /> Liste</span>, value: 'list' }
              ]}
            />
            <Segmented
              value={includeInactive}
              onChange={setIncludeInactive}
              options={[
                { label: '✅ Actives seulement', value: false },
                { label: '🗂️ Toutes', value: true }
              ]}
            />
          </Space>
        </div>

        <Row gutter={[12, 12]} style={{ marginTop: 16 }} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input.Search
              placeholder="Filtrer par nom ou slug..."
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={setSearch}
            />
          </Col>
          <Col xs={24} sm={12} md={16}>
            <Space wrap>
              <Button type="primary" style={{ backgroundColor: '#002c3e' }} icon={<FolderAddOutlined />} onClick={() => openCreateModal(null)}>
                Nouvelle famille
              </Button>
              {viewMode === 'tree' && (
                <>
                  <Button icon={<DownOutlined />} onClick={expandAll}>Déployer tout</Button>
                  <Button icon={<UpOutlined />} onClick={collapseAll}>Réduire tout</Button>
                </>
              )}
              <ExportButton type="categories" format="excel" filters={{ includeInactive }} />
            </Space>
          </Col>
        </Row>
      </Card>

      {viewMode === 'tree' ? (
        <Card variant="borderless">
          <Tree
            showLine
            blockNode
            expandedKeys={[...openNodes].map(String)}
            onExpand={(keys) => setOpenNodes(new Set(keys.map(Number)))}
            treeData={buildAntdTreeData(categories)}
            style={{ background: 'transparent' }}
          />
        </Card>
      ) : (
        <Card variant="borderless">
          <Table
            rowKey="id"
            dataSource={filterFlat(categories, search)}
            columns={listColumns}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            size="middle"
            loading={isLoading}
          />
        </Card>
      )}

      <Modal
        open={modalOpen}
        title={
          editMode
            ? `Modifier "${editMode.name}"`
            : parentId
              ? `Ajouter un enfant à ${findNodeById(viewMode === 'tree' ? categories : flatAllCategories, parentId)?.name || '...'}`
              : `Nouvelle famille / catégorie`
        }
        onOk={handleModalOk}
        onCancel={() => setModalOpen(false)}
        okText={editMode ? 'Enregistrer' : 'Créer'}
        cancelText="Annuler"
        width={640}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="name"
                label="Nom"
                rules={[{ required: true, message: 'Le nom est requis' }]}
              >
                <Input placeholder="Ex: Prises connectées" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="slug" label="Slug">
                <Input placeholder="prises-connectées (auto si vide)" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Description courte (optionnel)" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="type" label="Type">
                <Radio.Group optionType="button" buttonStyle="solid">
                  <Radio value="family">🏠 Famille</Radio>
                  <Radio value="criterion">📁 Critère</Radio>
                  <Radio value="value">🍃 Valeur</Radio>
                  <Radio value={null}>❔ Non défini</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="parentCategory" label="Catégorie parente">
                <Select
                  options={parentSelectOptions}
                  placeholder="Choisir un parent"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="color" label="Couleur">
                <Input
                  type="color"
                  style={{ width: 64, height: 32, padding: 0, border: '1px solid #d9d9d9', borderRadius: 6 }}
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="icon" label="Icône">
                <Input placeholder="fas fa-home / HomeOutlined" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="sortOrder" label="Ordre">
                <Input type="number" min={0} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="isActive" label="Statut" valuePropName="checked">
                <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Categories;
